import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Baby, UserCheck } from 'lucide-react';
import { CHILD_IMMIGRATION_STATUS } from '@/lib/qualificationOptions';

export interface Child {
  age: number;
  status: 'us_citizen' | 'green_card' | 'visa' | 'illegal' | 'unknown';
  lives_with: boolean;
}

interface ChildrenListProps {
  children: Child[];
  onChange: (children: Child[]) => void;
  disabled?: boolean;
}

export function ChildrenList({ children, onChange, disabled = false }: ChildrenListProps) {
  const addChild = () => {
    onChange([...children, { age: 0, status: 'unknown', lives_with: true }]);
  };

  const updateChild = (index: number, field: keyof Child, value: any) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeChild = (index: number) => {
    onChange(children.filter((_, i) => i !== index));
  };

  // Calculate summary
  const hasUsCitizen = children.some(c => c.status === 'us_citizen');
  const oldestAge = children.length > 0 ? Math.max(...children.map(c => c.age)) : null;
  const youngestAge = children.length > 0 ? Math.min(...children.map(c => c.age)) : null;

  return (
    <div className="space-y-3">
      {children.length > 0 && (
        <div className="space-y-2">
          {children.map((child, index) => (
            <div 
              key={index} 
              className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
            >
              <div className="flex items-center gap-1 text-sm text-muted-foreground w-20">
                <Baby className="w-4 h-4" />
                <span>Filho {index + 1}</span>
              </div>
              
              <div className="w-20">
                <Input
                  type="number"
                  min={0}
                  max={50}
                  placeholder="Idade"
                  value={child.age || ''}
                  onChange={(e) => updateChild(index, 'age', parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="h-8 text-sm"
                />
              </div>

              <Select
                value={child.status}
                onValueChange={(v) => updateChild(index, 'status', v)}
                disabled={disabled}
              >
                <SelectTrigger className="w-40 h-8">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {CHILD_IMMIGRATION_STATUS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={child.lives_with}
                  onCheckedChange={(v) => updateChild(index, 'lives_with', !!v)}
                  disabled={disabled}
                />
                <Label className="text-xs text-muted-foreground">Mora junto</Label>
              </div>

              {!disabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeChild(index)}
                  className="ml-auto"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {!disabled && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={addChild} 
          className="gap-1"
        >
          <Plus className="w-4 h-4" />
          Adicionar filho
        </Button>
      )}

      {/* Summary */}
      {children.length > 0 && (
        <div className="flex items-center gap-3 pt-2 border-t text-sm text-muted-foreground">
          <Badge variant="secondary" className="gap-1">
            <Baby className="w-3 h-3" />
            {children.length} {children.length === 1 ? 'filho' : 'filhos'}
          </Badge>
          {hasUsCitizen && (
            <Badge variant="default" className="gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              <UserCheck className="w-3 h-3" />
              Tem cidadão
            </Badge>
          )}
          {oldestAge !== null && (
            <span>Mais velho: {oldestAge} anos</span>
          )}
          {youngestAge !== null && oldestAge !== youngestAge && (
            <span>• Mais novo: {youngestAge} anos</span>
          )}
        </div>
      )}
    </div>
  );
}
